<template>
    <div :class="['w-full flex', toolTip ? 'flex-row' : 'flex-col', $attrs.class]">
        <CoreInput
            v-bind="$attrs"
            :name="$attrs.name || $attrs.id || $attrs.label || $attrs.placeholder"
            :label-wrapper-classes="'flex items-center justify-between mb-1' + (hideLabel ? ' sr-only' : '')"
            label-classes="block text-gray-900 dark:text-gray-300 text-sm font-medium leading-6"
            input-wrapper-classes="relative rounded-md shadow-xs"
            :input-classes="`${defaultInputClasses} ${inputClasses} ${adjustedInputClasses}`"
            input-invalid-classes="ring-red-600 ring-2"
            error-classes="mt-1 text-sm text-red-600"
            :type="(showPassword) ? 'text' : type"
            field-wrapper-classes="w-full">
            <template
                v-for="(_, slot) in $slots"
                #[slot]="scope">
                <slot
                    :name="slot"
                    v-bind="{...scope}" />
            </template>
            <template
                v-if="$slots.inputHelper"
                #inputHelper>
                <slot name="inputHelper" />
            </template>
            <template
                v-if="prependIconLocal || $slots.prependIcon || appendIconLocal || $slots.appendIcon"
                #inputIcon>
                <div
                    v-if="prependIconLocal || $slots.prependIcon"
                    class="absolute inset-y-0 flex items-center left-0 pl-2"
                    :class="{ 'cursor-pointer': prependIconClickable }"
                    @click="prependIconClick">
                    <slot name="prependIcon">
                        <Icon
                            :name="prependIconLocal"
                            :class="prependIconClass" />
                    </slot>
                </div>
                <div
                    v-if="appendIconLocal || $slots.appendIcon"
                    class="absolute inset-y-0 flex items-center right-0 pr-2"
                    :class="{ 'cursor-pointer': appendIconClickable || type === 'password' }"
                    @click="appendIconClick">
                    <slot name="appendIcon">
                        <Icon
                            :name="appendIconLocal"
                            :class="appendIconClass" />
                    </slot>
                </div>
            </template>
        </CoreInput>
        <div
            v-if="toolTip"
            class="pl-1 shrink">
            <BasePopover
                size="sm"
                :position="toolTip === true ? 'bottom-left' : toolTip">
                <template #trigger>
                    <div class="flex items-center h-9 mt-7">
                        <Icon
                            v-if="toolTip"
                            name="question-mark-circle"
                            size="5"
                            class="text-primary-500 hover:bg-primary-500 hover:text-white hover:rounded-full" />
                    </div>
                </template>
                <template #default>
                    <span class="text-neutral-500 dark:text-gray-300 mt-2">
                        {{ description }}
                    </span>
                </template>
            </BasePopover>
        </div>
        <div
            v-if="!toolTip && description"
            class="text-xs text-gray-500 dark:text-gray-300 mt-1"
            :class="{'underline sm:no-underline hover:underline cursor-pointer text-right': descriptionClickable}"
            @click="emits('descriptionClick')">
            {{ description }}
        </div>
    </div>
</template>

<script setup>
import {useSlots} from 'vue'

defineOptions({
    inheritAttrs: false
})

const defaultInputClasses = `block w-full rounded-md py-1.5 border-0 ring-1 ring-inset ring-gray-300 text-gray-900 shadow-xs placeholder:text-gray-400
                             appearance-none rounded w-full py-2 px-3 leading-tight
                             focus:ring-primary focus:ring-2 focus:ring-inset sm:text-sm focus:outline-hidden focus:shadow-outline
                             disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 disabled:shadow-none disabled:cursor-not-allowed
                             dark:text-gray-200 dark:placeholder:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-300 dark:disabled:ring-gray-400 dark:disabled:text-gray-400`

const props = defineProps({
    type: {
        type: String,
        default: 'text'
    },
    hideLabel: {
        type: Boolean,
        default: false
    },
    description: {
        type: String,
        default: null
    },
    descriptionClickable: {
        type: Boolean,
        default: false
    },
    toolTip: {
        type: Boolean || String,
        default: false,
        description: 'true == left || right || center'
    },
    inputClasses: {
        type: String,
        default: ''
    },
    prependIcon: {
        type: String,
        default: null
    },
    prependIconClass: {
        type: String,
        default: 'h-5 w-5 text-gray-500'
    },
    prependIconClickable: {
        type: Boolean,
        default: false
    },
    appendIcon: {
        type: String,
        default: null
    },
    appendIconClass: {
        type: String,
        default: 'h-5 w-5 text-gray-500'
    },
    appendIconClickable: {
        type: Boolean,
        default: false
    }
})

const emits = defineEmits(['prependIconClick', 'appendIconClick', 'descriptionClick'])

const showPassword = ref(false)

const prependIconLocal = computed(() => {
    return props.prependIcon
})

const appendIconLocal = computed(() => {
    if (props.type === 'password') {
        return 'eye' + (showPassword.value ? '-slash' : '')
    } else {
        return props.appendIcon
    }
})

const slots = useSlots()

const adjustedInputClasses = computed(() => {

    let adjustedInputClasses = ''

    if (prependIconLocal.value || slots.prependIcon) {
        adjustedInputClasses += ' pl-9'
    }

    if (appendIconLocal.value || slots.appendIcon) {
        adjustedInputClasses += ' pr-9'
    }

    return adjustedInputClasses
})

function prependIconClick() {
    emits('prependIconClick')
}

function appendIconClick() {
    if (props.type === 'password') {
        showPassword.value = !showPassword.value
    } else {
        emits('appendIconClick')
    }
}
</script>